import { useState, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, Autocomplete, Stack, Switch, FormControlLabel, Alert
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { downloadBlob, readFileAsText } from './csvUtils';

const SERVICE_TYPES = ['spa', 'salon', 'general'];
const EMPTY = {
  name: '', service_type: 'spa', price: '', validity_months: 3, sessions_total: 7,
  description: '', is_active: true, deductible_ids: [], non_deductible_ids: []
};

export default function PackagesTab({ rows, treatments, onRefresh, showSnackbar }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const uploadRef = useRef();

  const openAdd = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (row) => {
    setForm({
      name: row.name,
      service_type: row.service_type || 'spa',
      price: row.price,
      validity_months: row.validity_months || 1,
      sessions_total: row.sessions_total || 0,
      description: row.description || '',
      is_active: row.is_active !== 0,
      deductible_ids: row.deductible_ids || [],
      non_deductible_ids: row.non_deductible_ids || []
    });
    setEditId(row.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price) { showSnackbar('Name and price are required', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        validity_months: parseInt(form.validity_months) || 1,
        sessions_total: parseInt(form.sessions_total) || 0,
        is_active: form.is_active ? 1 : 0
      };
      if (editId) {
        await adminAPI.updatePackage(editId, payload);
        showSnackbar('Package updated');
      } else {
        await adminAPI.createPackage(payload);
        showSnackbar('Package created');
      }
      setOpen(false);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Save failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deletePackage(deleteId);
      showSnackbar('Package deleted');
      setDeleteId(null);
      onRefresh();
    } catch (e) {
      showSnackbar(e.response?.data?.message || 'Delete failed', 'error');
      setDeleteId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const res = await adminAPI.downloadPackagesCSV();
      downloadBlob(res.data, 'packages.csv');
    } catch { showSnackbar('CSV download failed', 'error'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    try {
      const csv = await readFileAsText(file);
      const res = await adminAPI.uploadPackagesCSV(csv);
      const { inserted, updated, errors } = res.data.data;
      showSnackbar(
        `Inserted: ${inserted}, Updated: ${updated}${errors?.length ? `, Errors: ${errors.length}` : ''}`,
        errors?.length ? 'warning' : 'success'
      );
      onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'CSV upload failed', 'error'); }
  };

  // Build lookup for treatment names by id
  const treatmentById = Object.fromEntries(treatments.map(t => [t.id, t]));
  const deductibleTreatments = treatments.filter(t => form.deductible_ids.includes(t.id));
  const nonDeductibleTreatments = treatments.filter(t => form.non_deductible_ids.includes(t.id));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Packages ({rows.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownloadCSV}>Download CSV</Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => uploadRef.current?.click()}>Upload CSV</Button>
          <input ref={uploadRef} type="file" accept=".csv" hidden onChange={handleUploadCSV} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAdd}>Add Package</Button>
        </Stack>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Packages give patients free sessions for a validity period. <strong>Free Treatments</strong> count against the session quota. <strong>Always Free</strong> items (e.g. Steam, Sauna) are never charged and never reduce the count.
      </Alert>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Price (₹)</strong></TableCell>
              <TableCell align="center"><strong>Validity</strong></TableCell>
              <TableCell align="center"><strong>Sessions</strong></TableCell>
              <TableCell><strong>Free Treatments (counted)</strong></TableCell>
              <TableCell><strong>Always Free (uncounted)</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 4 }}>No packages found</TableCell></TableRow>
            )}
            {rows.map(row => {
              const deductibleNames = (row.deductible_ids || []).map(id => treatmentById[id]?.name).filter(Boolean);
              const nonDeductibleNames = (row.non_deductible_ids || []).map(id => treatmentById[id]?.name).filter(Boolean);
              return (
                <TableRow key={row.id} hover sx={{ opacity: row.is_active ? 1 : 0.5 }}>
                  <TableCell><strong>{row.name}</strong></TableCell>
                  <TableCell>
                    <Chip label={row.service_type} size="small"
                      color={row.service_type === 'salon' ? 'secondary' : row.service_type === 'spa' ? 'primary' : 'default'} />
                  </TableCell>
                  <TableCell align="right">{parseFloat(row.price).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="center">{row.validity_months ? `${row.validity_months} mo` : '—'}</TableCell>
                  <TableCell align="center">{row.sessions_total || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {deductibleNames.slice(0, 3).map((n, i) => <Chip key={i} label={n} size="small" variant="outlined" color="primary" />)}
                      {deductibleNames.length > 3 && <Chip label={`+${deductibleNames.length - 3}`} size="small" />}
                      {!deductibleNames.length && <Typography variant="caption" color="text.secondary">None</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {nonDeductibleNames.slice(0, 2).map((n, i) => <Chip key={i} label={n} size="small" variant="outlined" color="success" />)}
                      {!nonDeductibleNames.length && <Typography variant="caption" color="text.secondary">None</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={row.is_active ? 'Yes' : 'No'} size="small" color={row.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteId(row.id)}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit Package' : 'Add Package'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Service Type</InputLabel>
            <Select value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} label="Service Type">
              {SERVICE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Price (₹) *" type="number" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} fullWidth />
            <TextField label="Validity (months)" type="number" value={form.validity_months}
              onChange={e => setForm(f => ({ ...f, validity_months: e.target.value }))} fullWidth inputProps={{ min: 1 }} />
            <TextField label="Free Sessions" type="number" value={form.sessions_total}
              onChange={e => setForm(f => ({ ...f, sessions_total: e.target.value }))} fullWidth inputProps={{ min: 0 }} />
          </Box>
          <TextField label="Description" value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />

          <Autocomplete
            multiple
            options={treatments.filter(t => !form.non_deductible_ids.includes(t.id))}
            getOptionLabel={t => `${t.name} (₹${t.price})`}
            value={deductibleTreatments}
            onChange={(_, val) => setForm(f => ({ ...f, deductible_ids: val.map(t => t.id) }))}
            renderInput={(params) => (
              <TextField {...params} label="Free Treatments (count against sessions)"
                helperText="Each use deducts 1 from the session quota — e.g. massages" />
            )}
            renderTags={(val, getTagProps) =>
              val.map((t, i) => <Chip key={t.id} label={t.name} size="small" color="primary" {...getTagProps({ index: i })} />)
            }
          />

          <Autocomplete
            multiple
            options={treatments.filter(t => !form.deductible_ids.includes(t.id))}
            getOptionLabel={t => `${t.name} (₹${t.price})`}
            value={nonDeductibleTreatments}
            onChange={(_, val) => setForm(f => ({ ...f, non_deductible_ids: val.map(t => t.id) }))}
            renderInput={(params) => (
              <TextField {...params} label="Always Free Treatments (never counted)"
                helperText="Always ₹0, never reduce session count — e.g. Steam, Sauna, Jacuzzi" />
            )}
            renderTags={(val, getTagProps) =>
              val.map((t, i) => <Chip key={t.id} label={t.name} size="small" color="success" {...getTagProps({ index: i })} />)
            }
          />

          <FormControlLabel
            control={<Switch checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />}
            label="Package is active (visible for purchase)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Package?</DialogTitle>
        <DialogContent>Patients who have already purchased this package keep their sessions. This removes the plan from the catalogue only.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
